<template>
  <transition name="fade-scale">
    <div v-if="show">
      <!--
        The dimming overlay is dropped on paid entry pages. A visitor arriving
        from an ad has not chosen to be here yet, and greeting them with a wall
        over the offer is the behaviour the campaign audit blamed for the bounce
        rate. The banner itself still appears and still gates consent; it just
        does not black out the page behind it.
      -->
      <div v-if="!unobtrusive" class="fixed inset-0 bg-black/60 z-[100] backdrop-blur-[2px]"></div>
      <div
        class="fixed bottom-0 inset-x-0 flex justify-center z-[110]"
        :class="unobtrusive ? 'px-2 pb-2 sm:px-4 sm:pb-4' : 'px-4 pb-4 sm:pb-6'"
      >
        <div
          class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full mx-auto border border-gray-100 dark:border-gray-700"
          :class="unobtrusive ? 'p-3 max-w-2xl' : 'p-4 max-w-lg'"
        >
          <div class="flex justify-between items-start" :class="unobtrusive ? 'sr-only' : ''">
            <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">🍪 {{ $t('consent.title') }}</h2>
            <button
                :aria-label="$t('consent.close_banner')"
                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                @click="rejectAll">
              <svg
                  class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
            </button>
          </div>
          <!--
            Compact mode has to stay short enough to sit under the page's primary
            action on a 667px phone, so the heading collapses to screen-reader
            text and the blurb is clamped rather than wrapping to four lines.
          -->
          <p
            class="text-gray-600 dark:text-gray-300"
            :class="unobtrusive ? 'mb-2 text-xs leading-snug line-clamp-2' : 'mt-2 mb-4'"
          >{{ $t('consent.description') }}</p>
          <div class="flex flex-row" :class="unobtrusive ? 'gap-2' : 'space-x-3 sm:space-x-4'">
            <button
                class="w-full bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                :class="unobtrusive ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'"
                @click="acceptAll"
            >
              {{ $t('consent.accept_all') }}
            </button>
            <button
                class="w-full bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                :class="unobtrusive ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'"
                @click="rejectAll"
            >
              {{ $t('consent.use_essential_only') }}
            </button>
          </div>
          <div class="text-center" :class="unobtrusive ? 'mt-1' : 'mt-2'">
            <button
                :aria-expanded="showCustom"
                aria-controls="cookie-options"
                class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded transition text-center"
                :class="unobtrusive ? 'px-2 py-0.5 text-xs' : 'px-4 py-2'"
                @click="toggleCustom"
            >
              {{ $t('consent.customize_preferences') }}
            </button>
          </div>
          <transition name="fade">
            <div v-if="showCustom" id="cookie-options" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
              <div class="mb-2">
                <p class="text-sm text-gray-600 dark:text-gray-400">{{ $t('consent.essential_description') }}</p>
              </div>
              <div v-for="category in categories" :key="category.name" class="flex items-center justify-between">
                <div>
                  <p class="font-medium text-gray-800 dark:text-gray-100 capitalize">{{ $t(category.labelKey) }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t(category.descriptionKey) }}</p>
                </div>
                <label :for="`toggle-${category.name}`" class="inline-flex items-center cursor-pointer">
                  <input
                      :id="`toggle-${category.name}`"
                      v-model="prefs[category.name]"
                      :aria-label="$t('consent.toggle_cookies', { category: $t(category.labelKey) })"
                      class="sr-only"
                      type="checkbox"
                  />
                  <span
                      :class="{'bg-blue-500': prefs[category.name], 'bg-gray-300 dark:bg-gray-600': !prefs[category.name]}"
                      class="w-10 h-6 rounded-full relative transition-colors duration-200">
                    <span
                        :class="{'translate-x-4': prefs[category.name]}"
                        class="absolute left-0 top-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200">
                    </span>
                  </span>
                </label>
              </div>
              <div class="flex justify-end mt-4">
                <button
                    class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                    @click="saveCustom"
                >
                  {{ $t('consent.save_preferences') }}
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useConsentStore } from '@/stores/consent'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const route = useRoute()
const store = useConsentStore()

/** Paid entry pages opt out of the dimming overlay via route meta. */
const unobtrusive = computed(() => !!route.meta?.consentUnobtrusive)
const showCustom = ref(false)
const prefs = ref({ analytics: false, marketing: false })

const show = computed(() => store.loaded && store.showBanner)
const categories = [{
  name: 'analytics', 
  labelKey: 'consent.categories.analytics', 
  descriptionKey: 'consent.categories.analytics_description',
}, {
  name: 'marketing', 
  labelKey: 'consent.categories.marketing', 
  descriptionKey: 'consent.categories.marketing_description',
},]

onMounted(async () => {
  if (store.showBanner) {
    prefs.value = {
      analytics: false, marketing: false,
    }
  } else {
    prefs.value = {
      analytics: store.consent.analytics, marketing: store.consent.marketing,
    }
  }
})

function acceptAll () {
  prefs.value = { analytics: true, marketing: true }
  store.acceptAll('banner')
}

function rejectAll () {
  prefs.value = { analytics: false, marketing: false }
  store.rejectAll('banner')
}

function toggleCustom () {
  showCustom.value = !showCustom.value
}

function saveCustom () {
  store.saveCustom(prefs.value, 'modal')
}
</script>

<style scoped>
.fade-scale-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-scale-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.fade-scale-enter-to {
  opacity: 1;
  transform: scale(1);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
}
</style>

